import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/api';
import { isHybridMode } from '../utils/config';

/**
 * Fetch full admin permissions for the logged-in user.
 * Returns the complete commands array, isOwner, isModerator, guildId, and a hasCommand helper.
 */
export default function useAdminPermissions() {
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [commands, setCommands] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [guildId, setGuildId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isHybridMode || !isLoggedIn) {
      setCommands([]);
      setIsOwner(false);
      setIsModerator(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      try {
        // Fetch guild config
        const configRes = await fetch(`${API_URL}/api/web/config`);
        const configData = await configRes.json();
        if (!cancelled && configData?.guild?.guild_id) {
          setGuildId(configData.guild.guild_id);
        }

        // Fetch permissions
        const permRes = await fetch(`${API_URL}/api/web/my-permissions`, {
          headers: getAuthHeaders(),
        });
        const permData = await permRes.json();
        if (!cancelled) {
          setCommands(permData?.commands || []);
          setIsOwner(!!permData?.is_owner);
          setIsModerator(!!permData?.is_moderator);
        }
      } catch {
        if (!cancelled) {
          setCommands([]);
          setIsOwner(false);
          setIsModerator(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [isLoggedIn, getAuthHeaders]);

  const hasCommand = useCallback(
    (cmd) => isOwner || commands.includes(cmd),
    [isOwner, commands]
  );

  const hasAnyCommand = useCallback(
    (...cmds) => isOwner || cmds.some((cmd) => commands.includes(cmd)),
    [isOwner, commands]
  );

  const hasAdminAccess = isOwner || commands.length > 0;

  return { commands, isOwner, isModerator, guildId, loading, hasCommand, hasAnyCommand, hasAdminAccess };
}
